export type TipoTendencia = 'radio' | 'programa' | 'locutor' | 'evento' | 'club';

export type DireccionTendencia = 'explosivo' | 'subiendo' | 'estable' | 'bajando';

export interface TendenciaEnVivo {
  id: number;
  tipo: TipoTendencia;
  nombre: string;
  subtitulo: string;
  tendenciaScore: number;
  crecimiento: number;
  direccion: DireccionTendencia;
  ultimaActualizacion: string;
  senalesActividad: {
    oyentes: number;
    interacciones: number;
    compartidos: number;
    comentarios: number;
    miembros?: number;
    participantes?: number;
  };
  posicion: number;
  cambioPosicion: number;
  imagenUrl: string;
  logoUrl: string;
  badge: string;
  estaEnVivo: boolean;
  frecuencia: string;
  horario: string;
  ciudad: string;
  pais: string;
  categoria: string;
  radioNombre: string;
  locutorNombre: string;
  miembros: number;
}

export interface ReglaTendencia {
  id: number;
  titulo: string;
  descripcion: string;
  icono: string;
  gradiente: string;
  colorBorde: string;
}

export interface StatsTendencias {
  totalTendencias: number;
  categoriasActivas: number;
  tendenciasExplosivas: number;
  actualizacionesUltimaHora: number;
  categoriaMasCaliente: string;
  itemTop: string;
}

export const statsTendencias: StatsTendencias = {
  totalTendencias: 25,
  categoriasActivas: 5,
  tendenciasExplosivas: 7,
  actualizacionesUltimaHora: 34,
  categoriaMasCaliente: 'Locutores',
  itemTop: 'Andrés Castillo',
};

export const reglasTendencias: ReglaTendencia[] = [
  {
    id: 1,
    titulo: 'Basado en Actividad Real, No Manual',
    descripcion: 'Cada posición en el ranking de tendencias se calcula a partir de señales objetivas: oyentes activos, interacciones en tiempo real, compartidos, comentarios y crecimiento porcentual. Nadie puede comprar una posición ni editarla manualmente. La comunidad define qué es tendencia.',
    icono: 'ri-line-chart-line',
    gradiente: 'from-rose-500 to-amber-500',
    colorBorde: 'border-rose-200',
  },
  {
    id: 2,
    titulo: 'Actualización Automática Cada 60 Segundos',
    descripcion: 'El sistema recalcula todas las posiciones cada minuto basándose en el delta de actividad. Si una radio duplica sus oyentes en 5 minutos, sube. Si un locutor genera 500 comentarios en una hora, escala. Sin intervención humana. Sin demoras.',
    icono: 'ri-refresh-line',
    gradiente: 'from-emerald-500 to-teal-500',
    colorBorde: 'border-emerald-200',
  },
  {
    id: 3,
    titulo: 'Las 5 Categorías Siempre Visibles',
    descripcion: 'Radio, Programa, Locutor, Evento y Club tienen cada uno su espacio garantizado en el bloque de tendencias. Ninguna categoría puede ser ocultada, minimizada ni desplazada. La plataforma muestra tendencia en todas las dimensiones, no solo en radios.',
    icono: 'ri-layout-grid-line',
    gradiente: 'from-violet-500 to-purple-500',
    colorBorde: 'border-violet-200',
  },
  {
    id: 4,
    titulo: 'Las Tendencias Deben Impulsar Descubrimiento',
    descripcion: 'Cada item en tendencia es un puente hacia nuevo contenido. Un clic en una Radio Tendencia abre su perfil. Un clic en un Programa Tendencia muestra su programación. Las tendencias no son un ranking estático — son la puerta de entrada a lo que está pasando ahora.',
    icono: 'ri-compass-3-line',
    gradiente: 'from-amber-500 to-orange-500',
    colorBorde: 'border-amber-200',
  },
];

export const tiposTendencia: { id: TipoTendencia; label: string; icono: string; color: string; bg: string }[] = [
  { id: 'radio', label: 'Radio', icono: 'ri-radio-line', color: 'text-rose-600', bg: 'bg-rose-50' },
  { id: 'programa', label: 'Programa', icono: 'ri-mic-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { id: 'locutor', label: 'Locutor', icono: 'ri-user-voice-line', color: 'text-amber-600', bg: 'bg-amber-50' },
  { id: 'evento', label: 'Evento', icono: 'ri-calendar-event-line', color: 'text-violet-600', bg: 'bg-violet-50' },
  { id: 'club', label: 'Club', icono: 'ri-group-line', color: 'text-sky-600', bg: 'bg-sky-50' },
];

export const direccionConfig: Record<DireccionTendencia, { label: string; color: string; icono: string; bg: string; textoColor: string }> = {
  explosivo: { label: 'Explosivo', color: 'bg-rose-500', icono: 'ri-fire-line', bg: 'bg-rose-50', textoColor: 'text-rose-600' },
  subiendo: { label: 'Subiendo', color: 'bg-emerald-500', icono: 'ri-arrow-up-line', bg: 'bg-emerald-50', textoColor: 'text-emerald-600' },
  estable: { label: 'Estable', color: 'bg-amber-500', icono: 'ri-subtract-line', bg: 'bg-amber-50', textoColor: 'text-amber-600' },
  bajando: { label: 'Bajando', color: 'bg-stone-400', icono: 'ri-arrow-down-line', bg: 'bg-stone-50', textoColor: 'text-stone-500' },
};

export const tendenciasEnVivo: TendenciaEnVivo[] = [
  {
    id: 1,
    tipo: 'radio',
    nombre: 'Rock Total Radio',
    subtitulo: '87.01 RFW · Rock',
    tendenciaScore: 98,
    crecimiento: 47,
    direccion: 'explosivo',
    ultimaActualizacion: '2026-06-21T14:32:00Z',
    senalesActividad: { oyentes: 12800, interacciones: 3400, compartidos: 1850, comentarios: 4200 },
    posicion: 1,
    cambioPosicion: 8,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20rock%20radio%20station%20broadcast%20studio%20with%20electric%20guitars%20on%20wall%20and%20vintage%20vinyl%20collection%2C%20warm%20amber%20studio%20lighting%2C%20professional%20microphone%20setup%2C%20energetic%20rock%20atmosphere%2C%20editorial%20photography%20with%20soft%20shadows%20and%20warm%20color%20tones%2C%20industrial%20loft%20aesthetic&width=800&height=500&seq=tendencia-radio-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%2C%20circular%20design%20black%20and%20red%20color%20scheme%2C%20modern%20rock%20broadcast%20emblem%2C%20clean%20vector%20style%20isolated%20on%20transparent%20background&width=200&height=200&seq=tendencia-logo-rd-01&orientation=squarish',
    badge: 'Crecimiento Explosivo',
    estaEnVivo: true,
    frecuencia: '87.01 RFW',
    horario: '24/7',
    ciudad: 'Buenos Aires',
    pais: 'Argentina',
    categoria: 'Rock',
    radioNombre: 'Rock Total Radio',
    locutorNombre: 'Andrés Castillo',
    miembros: 8920,
  },
  {
    id: 2,
    tipo: 'locutor',
    nombre: 'Andrés Castillo',
    subtitulo: 'Rock Total Radio · 87.01 RFW',
    tendenciaScore: 96,
    crecimiento: 62,
    direccion: 'explosivo',
    ultimaActualizacion: '2026-06-21T14:31:00Z',
    senalesActividad: { oyentes: 8900, interacciones: 5200, compartidos: 3100, comentarios: 6800 },
    posicion: 2,
    cambioPosicion: 12,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20male%20radio%20host%20speaking%20passionately%20into%20professional%20microphone%20in%20modern%20studio%2C%20warm%20studio%20lighting%20with%20amber%20tones%2C%20headphones%20around%20neck%2C%20expressive%20gesture%2C%20editorial%20portrait%20photography%20with%20shallow%20depth%20of%20field%2C%20clean%20background&width=800&height=500&seq=tendencia-locutor-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Male%20radio%20broadcaster%20avatar%20portrait%20with%20headphones%2C%20friendly%20expression%2C%20circular%20frame%2C%20warm%20lighting%2C%20professional%20headshot%20style%20isolated%20on%20light%20background&width=200&height=200&seq=tendencia-logo-lc-01&orientation=squarish',
    badge: 'Locutor del Momento',
    estaEnVivo: true,
    frecuencia: '87.01 RFW',
    horario: 'Lun-Vie 16:00-20:00',
    ciudad: 'Buenos Aires',
    pais: 'Argentina',
    categoria: 'Rock',
    radioNombre: 'Rock Total Radio',
    locutorNombre: 'Andrés Castillo',
    miembros: 0,
  },
  {
    id: 3,
    tipo: 'programa',
    nombre: 'Distorsión Total',
    subtitulo: 'Rock Total Radio · Lun-Vie 16:00',
    tendenciaScore: 94,
    crecimiento: 55,
    direccion: 'explosivo',
    ultimaActualizacion: '2026-06-21T14:30:00Z',
    senalesActividad: { oyentes: 7200, interacciones: 4100, compartidos: 2600, comentarios: 5500 },
    posicion: 3,
    cambioPosicion: 6,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Live%20rock%20radio%20program%20studio%20with%20host%20behind%20mixing%20console%2C%20electric%20guitar%20collection%20on%20wall%2C%20warm%20red%20and%20amber%20mood%20lighting%2C%20professional%20broadcast%20equipment%2C%20energetic%20atmosphere%2C%20editorial%20photography&width=800&height=500&seq=tendencia-prog-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20program%20show%20logo%20with%20distorted%20guitar%20waveform%20design%2C%20bold%20typography%2C%20red%20and%20black%20color%20scheme%2C%20circular%20emblem%2C%20clean%20vector%20style&width=200&height=200&seq=tendencia-logo-pg-01&orientation=squarish',
    badge: 'Programa #1 del Día',
    estaEnVivo: true,
    frecuencia: '87.01 RFW',
    horario: 'Lun-Vie 16:00-20:00',
    ciudad: 'Buenos Aires',
    pais: 'Argentina',
    categoria: 'Rock',
    radioNombre: 'Rock Total Radio',
    locutorNombre: 'Andrés Castillo',
    miembros: 4500,
  },
  {
    id: 4,
    tipo: 'evento',
    nombre: 'Final del Campeonato Nacional',
    subtitulo: 'Deportes Total Radio · 21 Jun 2026',
    tendenciaScore: 93,
    crecimiento: 71,
    direccion: 'explosivo',
    ultimaActualizacion: '2026-06-21T14:29:00Z',
    senalesActividad: { oyentes: 0, interacciones: 7800, compartidos: 5100, comentarios: 9200, participantes: 15600 },
    posicion: 4,
    cambioPosicion: 15,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Soccer%20stadium%20packed%20with%20passionate%20fans%20waving%20flags%20under%20dramatic%20sunset%20sky%2C%20championship%20final%20match%20atmosphere%2C%20stadium%20floodlights%20glowing%2C%20confetti%20and%20banners%20in%20air%2C%20wide%20angle%20sports%20photography%20with%20vibrant%20colors&width=800&height=500&seq=tendencia-evento-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%2C%20circular%20design%20dark%20navy%20and%20gold%20color%20scheme%2C%20clean%20vector%20emblem%20style&width=200&height=200&seq=tendencia-logo-ev-01&orientation=squarish',
    badge: 'Evento Masivo',
    estaEnVivo: true,
    frecuencia: '120.00 RFW',
    horario: 'Hoy 15:00',
    ciudad: 'Dallas',
    pais: 'Estados Unidos',
    categoria: 'Deportes',
    radioNombre: 'Deportes Total Radio',
    locutorNombre: 'Javier Solís',
    miembros: 0,
  },
  {
    id: 5,
    tipo: 'club',
    nombre: 'Rockeros Unidos',
    subtitulo: 'Rock Total Radio · Club Premium',
    tendenciaScore: 91,
    crecimiento: 43,
    direccion: 'explosivo',
    ultimaActualizacion: '2026-06-21T14:28:00Z',
    senalesActividad: { oyentes: 0, interacciones: 2900, compartidos: 1600, comentarios: 3800, miembros: 8920 },
    posicion: 5,
    cambioPosicion: 4,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Rock%20music%20fan%20club%20gathering%20in%20industrial%20loft%20venue%2C%20people%20wearing%20band%20t-shirts%20socializing%2C%20warm%20amber%20lighting%20with%20string%20lights%2C%20vintage%20concert%20posters%20on%20wall%2C%20community%20atmosphere%2C%20documentary%20photography%20style&width=800&height=500&seq=tendencia-club-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Rock%20music%20fan%20club%20logo%20with%20guitar%20pick%20and%20crown%20design%2C%20circular%20emblem%2C%20black%20and%20gold%20color%20scheme%2C%20premium%20community%20badge%20style&width=200&height=200&seq=tendencia-logo-cl-01&orientation=squarish',
    badge: 'Club Más Activo',
    estaEnVivo: false,
    frecuencia: '87.01 RFW',
    horario: 'Activo 24/7',
    ciudad: 'Buenos Aires',
    pais: 'Argentina',
    categoria: 'Rock',
    radioNombre: 'Rock Total Radio',
    locutorNombre: '',
    miembros: 8920,
  },
  {
    id: 6,
    tipo: 'radio',
    nombre: 'La Mexicana FM',
    subtitulo: '103.50 RFW · Regional Mexicana',
    tendenciaScore: 89,
    crecimiento: 38,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:27:00Z',
    senalesActividad: { oyentes: 10500, interacciones: 2800, compartidos: 1400, comentarios: 3500 },
    posicion: 6,
    cambioPosicion: 3,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20studio%20with%20colorful%20decorations%20and%20traditional%20instruments%2C%20warm%20festive%20lighting%2C%20professional%20broadcast%20setup%2C%20cultural%20atmosphere%2C%20editorial%20photography%20with%20vibrant%20colors&width=800&height=500&seq=tendencia-radio-02&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20with%20guitar%20and%20sombrero%20elements%2C%20circular%20design%20warm%20orange%20and%20green%20colors%2C%20clean%20modern%20broadcast%20style&width=200&height=200&seq=tendencia-logo-rd-02&orientation=squarish',
    badge: 'En Ascenso',
    estaEnVivo: true,
    frecuencia: '103.50 RFW',
    horario: '24/7',
    ciudad: 'Houston',
    pais: 'Estados Unidos',
    categoria: 'Regional Mexicana',
    radioNombre: 'La Mexicana FM',
    locutorNombre: 'Don Fernando Ramírez',
    miembros: 7200,
  },
  {
    id: 7,
    tipo: 'locutor',
    nombre: 'Valentina Ríos',
    subtitulo: 'Tardes con Vale · 94.70 RFW',
    tendenciaScore: 87,
    crecimiento: 54,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:26:00Z',
    senalesActividad: { oyentes: 6500, interacciones: 4600, compartidos: 2700, comentarios: 5900 },
    posicion: 7,
    cambioPosicion: 9,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Energetic%20female%20radio%20host%20laughing%20in%20bright%20modern%20studio%2C%20colorful%20headphones%2C%20natural%20daylight%20streaming%20through%20window%2C%20warm%20and%20inviting%20atmosphere%2C%20editorial%20portrait%20photography%20with%20soft%20natural%20light&width=800&height=500&seq=tendencia-locutor-02&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Female%20radio%20broadcaster%20avatar%20portrait%20with%20headphones%2C%20warm%20smile%2C%20circular%20frame%2C%20soft%20lighting%2C%20professional%20headshot%20style&width=200&height=200&seq=tendencia-logo-lc-02&orientation=squarish',
    badge: 'Revelación de la Semana',
    estaEnVivo: true,
    frecuencia: '94.70 RFW',
    horario: 'Lun-Vie 14:00-18:00',
    ciudad: 'Ciudad de México',
    pais: 'México',
    categoria: 'Pop',
    radioNombre: 'Radio Nueva Actitud',
    locutorNombre: 'Valentina Ríos',
    miembros: 0,
  },
  {
    id: 8,
    tipo: 'programa',
    nombre: 'El Sabor del Mediodía',
    subtitulo: 'La Mexicana FM · Lun-Vie 12:00',
    tendenciaScore: 86,
    crecimiento: 49,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:25:00Z',
    senalesActividad: { oyentes: 5800, interacciones: 3200, compartidos: 1900, comentarios: 4100 },
    posicion: 8,
    cambioPosicion: 7,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Mexican%20culinary%20radio%20show%20studio%20with%20host%20and%20guest%20chef%2C%20colorful%20Mexican%20decorations%2C%20warm%20kitchen-like%20atmosphere%2C%20professional%20microphones%2C%20festive%20lighting%2C%20editorial%20photography&width=800&height=500&seq=tendencia-prog-02&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20food%20and%20music%20radio%20program%20logo%20with%20sombrero%20and%20microphone%2C%20warm%20colors%2C%20circular%20emblem%2C%20festive%20style&width=200&height=200&seq=tendencia-logo-pg-02&orientation=squarish',
    badge: 'Programa Viral',
    estaEnVivo: true,
    frecuencia: '103.50 RFW',
    horario: 'Lun-Vie 12:00-14:00',
    ciudad: 'Houston',
    pais: 'Estados Unidos',
    categoria: 'Regional Mexicana',
    radioNombre: 'La Mexicana FM',
    locutorNombre: 'Don Fernando Ramírez',
    miembros: 3800,
  },
  {
    id: 9,
    tipo: 'club',
    nombre: 'Soneros de Corazón',
    subtitulo: 'Radio Salsa Brava · Club Oficial',
    tendenciaScore: 84,
    crecimiento: 36,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:24:00Z',
    senalesActividad: { oyentes: 0, interacciones: 2400, compartidos: 1300, comentarios: 3100, miembros: 6700 },
    posicion: 9,
    cambioPosicion: 5,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Salsa%20dance%20club%20social%20event%20with%20couples%20dancing%20under%20warm%20colored%20lights%2C%20Latin%20Caribbean%20atmosphere%2C%20live%20band%20in%20background%2C%20festive%20community%20gathering%2C%20vibrant%20documentary%20photography&width=800&height=500&seq=tendencia-club-02&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Salsa%20music%20fan%20club%20logo%20with%20conga%20drums%20and%20tropical%20palm%20design%2C%20circular%20emblem%2C%20orange%20and%20yellow%20colors%2C%20Caribbean%20style&width=200&height=200&seq=tendencia-logo-cl-02&orientation=squarish',
    badge: 'Comunidad Vibrante',
    estaEnVivo: false,
    frecuencia: '110.70 RFW',
    horario: 'Activo 24/7',
    ciudad: 'San Juan',
    pais: 'Puerto Rico',
    categoria: 'Salsa',
    radioNombre: 'Radio Salsa Brava',
    locutorNombre: '',
    miembros: 6700,
  },
  {
    id: 10,
    tipo: 'locutor',
    nombre: 'Carlos Vega',
    subtitulo: 'Radio Salsa Brava · 110.70 RFW',
    tendenciaScore: 83,
    crecimiento: 31,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:23:00Z',
    senalesActividad: { oyentes: 5500, interacciones: 3100, compartidos: 1800, comentarios: 3900 },
    posicion: 10,
    cambioPosicion: 2,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20Latin%20male%20radio%20host%20with%20warm%20smile%20in%20tropical-themed%20studio%2C%20colorful%20shirt%2C%20professional%20microphone%2C%20Caribbean%20vibe%20decor%2C%20warm%20amber%20lighting%2C%20editorial%20portrait&width=800&height=500&seq=tendencia-locutor-03&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Latin%20male%20radio%20broadcaster%20avatar%20with%20headphones%2C%20friendly%20expression%2C%20circular%20frame%2C%20warm%20tropical%20vibe&width=200&height=200&seq=tendencia-logo-lc-03&orientation=squarish',
    badge: 'Voz del Caribe',
    estaEnVivo: true,
    frecuencia: '110.70 RFW',
    horario: 'Lun-Sáb 10:00-14:00',
    ciudad: 'San Juan',
    pais: 'Puerto Rico',
    categoria: 'Salsa',
    radioNombre: 'Radio Salsa Brava',
    locutorNombre: 'Carlos Vega',
    miembros: 0,
  },
  {
    id: 11,
    tipo: 'radio',
    nombre: 'Electro Vibes FM',
    subtitulo: '96.50 RFW · Electrónica',
    tendenciaScore: 82,
    crecimiento: 52,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:22:00Z',
    senalesActividad: { oyentes: 9400, interacciones: 3700, compartidos: 2100, comentarios: 4800 },
    posicion: 11,
    cambioPosicion: 10,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Futuristic%20electronic%20music%20radio%20studio%20with%20neon%20cyan%20and%20magenta%20lighting%2C%20DJ%20mixing%20console%2C%20synthesizer%20equipment%2C%20modern%20minimalist%20aesthetic%2C%20club%20atmosphere%2C%20editorial%20photography&width=800&height=500&seq=tendencia-radio-03&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Electronic%20music%20radio%20station%20logo%20with%20synthesizer%20waveform%20and%20neon%20accents%2C%20circular%20design%20vibrant%20cyan%20and%20magenta%20colors%2C%20futuristic%20style&width=200&height=200&seq=tendencia-logo-rd-03&orientation=squarish',
    badge: 'Crecimiento Semanal',
    estaEnVivo: true,
    frecuencia: '96.50 RFW',
    horario: '24/7',
    ciudad: 'Miami',
    pais: 'Estados Unidos',
    categoria: 'Electrónica',
    radioNombre: 'Electro Vibes FM',
    locutorNombre: 'DJ Spark',
    miembros: 5600,
  },
  {
    id: 12,
    tipo: 'programa',
    nombre: 'Mic Check Live',
    subtitulo: 'Radio Hip Hop Nation · Lun-Vie 20:00',
    tendenciaScore: 80,
    crecimiento: 44,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:21:00Z',
    senalesActividad: { oyentes: 5100, interacciones: 3800, compartidos: 2400, comentarios: 6200 },
    posicion: 12,
    cambioPosicion: 11,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Hip%20hop%20radio%20show%20with%20two%20hosts%20in%20urban%20style%20studio%2C%20graffiti%20art%20on%20walls%2C%20turntables%20and%20microphones%2C%20purple%20and%20gold%20mood%20lighting%2C%20street%20culture%20vibe%2C%20editorial%20photography&width=800&height=500&seq=tendencia-prog-03&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Hip%20hop%20radio%20program%20logo%20with%20microphone%20and%20turntable%2C%20bold%20typography%2C%20gold%20and%20black%20colors%2C%20urban%20style&width=200&height=200&seq=tendencia-logo-pg-03&orientation=squarish',
    badge: 'Audiencia Récord',
    estaEnVivo: true,
    frecuencia: '170.40 RFW',
    horario: 'Lun-Vie 20:00-23:00',
    ciudad: 'Atlanta',
    pais: 'Estados Unidos',
    categoria: 'Hip Hop',
    radioNombre: 'Radio Hip Hop Nation',
    locutorNombre: 'Big Mike & DJ Scratch',
    miembros: 4200,
  },
  {
    id: 13,
    tipo: 'evento',
    nombre: 'Noche de Leyendas Urbanas',
    subtitulo: 'Radio Hip Hop Nation · 25 Jun 2026',
    tendenciaScore: 79,
    crecimiento: 58,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:20:00Z',
    senalesActividad: { oyentes: 0, interacciones: 4200, compartidos: 2800, comentarios: 5100, participantes: 8900 },
    posicion: 13,
    cambioPosicion: 18,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Urban%20legends%20themed%20night%20event%20with%20storytellers%20on%20dimly%20lit%20stage%2C%20mysterious%20atmosphere%20with%20purple%20and%20amber%20uplighting%2C%20captivated%20audience%2C%20theatrical%20setting%2C%20dramatic%20photography&width=800&height=500&seq=tendencia-evento-02&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Hip%20hop%20radio%20station%20logo%20with%20turntable%20and%20graffiti%20crown%2C%20circular%20design%20gold%20and%20black%20colors%2C%20urban%20broadcast%20emblem&width=200&height=200&seq=tendencia-logo-ev-02&orientation=squarish',
    badge: 'Próximo Imperdible',
    estaEnVivo: false,
    frecuencia: '170.40 RFW',
    horario: '25 Jun 20:00',
    ciudad: 'Atlanta',
    pais: 'Estados Unidos',
    categoria: 'Hip Hop',
    radioNombre: 'Radio Hip Hop Nation',
    locutorNombre: 'Big Mike',
    miembros: 0,
  },
  {
    id: 14,
    tipo: 'club',
    nombre: 'Fiebre Electrónica',
    subtitulo: 'Electro Vibes FM · Club Exclusivo',
    tendenciaScore: 77,
    crecimiento: 29,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:19:00Z',
    senalesActividad: { oyentes: 0, interacciones: 2100, compartidos: 1100, comentarios: 2600, miembros: 5600 },
    posicion: 14,
    cambioPosicion: 3,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Electronic%20music%20fan%20community%20meetup%20with%20neon%20decorations%20and%20LED%20visuals%2C%20people%20dancing%20and%20socializing%2C%20futuristic%20club%20atmosphere%2C%20cyan%20and%20magenta%20mood%20lighting%2C%20modern%20event%20photography&width=800&height=500&seq=tendencia-club-03&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Electronic%20music%20fan%20club%20logo%20with%20sound%20wave%20and%20neon%20triangle%2C%20circular%20emblem%2C%20cyan%20and%20magenta%20colors&width=200&height=200&seq=tendencia-logo-cl-03&orientation=squarish',
    badge: 'Club del Mes',
    estaEnVivo: false,
    frecuencia: '96.50 RFW',
    horario: 'Activo 24/7',
    ciudad: 'Miami',
    pais: 'Estados Unidos',
    categoria: 'Electrónica',
    radioNombre: 'Electro Vibes FM',
    locutorNombre: '',
    miembros: 5600,
  },
  {
    id: 15,
    tipo: 'radio',
    nombre: 'Deportes Total Radio',
    subtitulo: '120.00 RFW · Deportes',
    tendenciaScore: 76,
    crecimiento: 34,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:18:00Z',
    senalesActividad: { oyentes: 8700, interacciones: 2900, compartidos: 1600, comentarios: 3800 },
    posicion: 15,
    cambioPosicion: 1,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Professional%20sports%20radio%20studio%20with%20multiple%20large%20screens%20showing%20live%20matches%2C%20trophy%20cabinet%2C%20dynamic%20broadcast%20atmosphere%2C%20professional%20lighting%2C%20editorial%20photography&width=800&height=500&seq=tendencia-radio-04&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%2C%20circular%20design%20navy%20and%20gold%20color%20scheme&width=200&height=200&seq=tendencia-logo-rd-04&orientation=squarish',
    badge: 'Cobertura Especial',
    estaEnVivo: true,
    frecuencia: '120.00 RFW',
    horario: '24/7',
    ciudad: 'Dallas',
    pais: 'Estados Unidos',
    categoria: 'Deportes',
    radioNombre: 'Deportes Total Radio',
    locutorNombre: 'Javier Solís',
    miembros: 6300,
  },
  {
    id: 16,
    tipo: 'programa',
    nombre: 'Tardes con Vale',
    subtitulo: 'Radio Nueva Actitud · Lun-Vie 14:00',
    tendenciaScore: 74,
    crecimiento: 41,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:17:00Z',
    senalesActividad: { oyentes: 4300, interacciones: 3600, compartidos: 2000, comentarios: 4700 },
    posicion: 16,
    cambioPosicion: 5,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Bright%20afternoon%20radio%20show%20with%20female%20host%20in%20colorful%20modern%20studio%2C%20plants%20and%20natural%20decor%2C%20sunny%20window%20light%2C%20positive%20vibe%2C%20lifestyle%20editorial%20photography&width=800&height=500&seq=tendencia-prog-04&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Modern%20pop%20radio%20program%20logo%20with%20headphones%20and%20sparkles%2C%20pink%20and%20coral%20colors%2C%20youthful%20style%2C%20circular%20emblem&width=200&height=200&seq=tendencia-logo-pg-04&orientation=squarish',
    badge: 'Creciendo Rápido',
    estaEnVivo: true,
    frecuencia: '94.70 RFW',
    horario: 'Lun-Vie 14:00-18:00',
    ciudad: 'Ciudad de México',
    pais: 'México',
    categoria: 'Pop',
    radioNombre: 'Radio Nueva Actitud',
    locutorNombre: 'Valentina Ríos',
    miembros: 3100,
  },
  {
    id: 17,
    tipo: 'locutor',
    nombre: 'Diego Montenegro',
    subtitulo: 'Radio Hip Hop Nation · 170.40 RFW',
    tendenciaScore: 73,
    crecimiento: 39,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:16:00Z',
    senalesActividad: { oyentes: 4200, interacciones: 3500, compartidos: 2200, comentarios: 5100 },
    posicion: 17,
    cambioPosicion: 13,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Young%20urban%20male%20radio%20host%20with%20confident%20expression%2C%20modern%20streetwear%20style%2C%20professional%20headphones%2C%20graffiti%20art%20background%2C%20warm%20golden%20lighting%2C%20editorial%20portrait&width=800&height=500&seq=tendencia-locutor-04&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Young%20urban%20male%20broadcaster%20avatar%20with%20cap%20and%20headphones%2C%20street%20style%2C%20circular%20frame&width=200&height=200&seq=tendencia-logo-lc-04&orientation=squarish',
    badge: 'Nuevo Talento',
    estaEnVivo: true,
    frecuencia: '170.40 RFW',
    horario: 'Lun-Vie 20:00-23:00',
    ciudad: 'Atlanta',
    pais: 'Estados Unidos',
    categoria: 'Hip Hop',
    radioNombre: 'Radio Hip Hop Nation',
    locutorNombre: 'Diego Montenegro',
    miembros: 0,
  },
  {
    id: 18,
    tipo: 'evento',
    nombre: 'Summer Fest RFW 2026',
    subtitulo: 'RFW · 15 Julio 2026',
    tendenciaScore: 72,
    crecimiento: 67,
    direccion: 'explosivo',
    ultimaActualizacion: '2026-06-21T14:15:00Z',
    senalesActividad: { oyentes: 0, interacciones: 5600, compartidos: 3800, comentarios: 7400, participantes: 18500 },
    posicion: 18,
    cambioPosicion: 22,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Massive%20outdoor%20summer%20music%20festival%20with%20spectacular%20stage%20and%20LED%20screens%2C%20huge%20crowd%20dancing%20at%20golden%20hour%2C%20colorful%20confetti%20and%20smoke%20effects%2C%20epic%20wide%20angle%20festival%20photography&width=800&height=500&seq=tendencia-evento-03&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Summer%20music%20festival%20logo%20with%20sun%20and%20palm%20tree%2C%20circular%20design%20with%20warm%20gradient%20colors%2C%20festive%20emblem&width=200&height=200&seq=tendencia-logo-ev-03&orientation=squarish',
    badge: 'Evento del Año',
    estaEnVivo: false,
    frecuencia: 'RFW',
    horario: '15 Jul 16:00',
    ciudad: 'Miami',
    pais: 'Estados Unidos',
    categoria: 'Festival',
    radioNombre: 'RFW',
    locutorNombre: '',
    miembros: 0,
  },
  {
    id: 19,
    tipo: 'club',
    nombre: 'Legión Deportiva',
    subtitulo: 'Deportes Total Radio · Club Oficial',
    tendenciaScore: 70,
    crecimiento: 33,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:14:00Z',
    senalesActividad: { oyentes: 0, interacciones: 1900, compartidos: 1050, comentarios: 2400, miembros: 6300 },
    posicion: 19,
    cambioPosicion: 2,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Sports%20fan%20club%20watch%20party%20with%20fans%20in%20team%20jerseys%20cheering%20at%20large%20screen%2C%20excited%20atmosphere%2C%20flags%20and%20banners%2C%20community%20sports%20event%2C%20documentary%20photography&width=800&height=500&seq=tendencia-club-04&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20fan%20club%20logo%20with%20shield%20and%20star%20design%2C%20circular%20emblem%2C%20navy%20and%20gold%20colors&width=200&height=200&seq=tendencia-logo-cl-04&orientation=squarish',
    badge: 'Comunidad Leal',
    estaEnVivo: false,
    frecuencia: '120.00 RFW',
    horario: 'Activo 24/7',
    ciudad: 'Dallas',
    pais: 'Estados Unidos',
    categoria: 'Deportes',
    radioNombre: 'Deportes Total Radio',
    locutorNombre: '',
    miembros: 6300,
  },
  {
    id: 20,
    tipo: 'radio',
    nombre: 'Radio Salsa Brava',
    subtitulo: '110.70 RFW · Salsa',
    tendenciaScore: 69,
    crecimiento: 22,
    direccion: 'estable',
    ultimaActualizacion: '2026-06-21T14:13:00Z',
    senalesActividad: { oyentes: 7600, interacciones: 2200, compartidos: 1200, comentarios: 2900 },
    posicion: 20,
    cambioPosicion: -2,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Tropical%20salsa%20radio%20studio%20with%20conga%20drums%20and%20colorful%20Caribbean%20decor%2C%20warm%20sunset%20lighting%2C%20Latin%20music%20posters%20on%20wall%2C%20vibrant%20atmosphere%2C%20editorial%20photography&width=800&height=500&seq=tendencia-radio-05&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Salsa%20music%20radio%20station%20logo%20with%20congas%20and%20tropical%20palm%2C%20circular%20design%20orange%20and%20yellow%20colors&width=200&height=200&seq=tendencia-logo-rd-05&orientation=squarish',
    badge: 'Líder Constante',
    estaEnVivo: true,
    frecuencia: '110.70 RFW',
    horario: '24/7',
    ciudad: 'San Juan',
    pais: 'Puerto Rico',
    categoria: 'Salsa',
    radioNombre: 'Radio Salsa Brava',
    locutorNombre: 'Carlos Vega',
    miembros: 7800,
  },
  {
    id: 21,
    tipo: 'programa',
    nombre: 'Bass Drop Session',
    subtitulo: 'Electro Vibes FM · Sáb 22:00',
    tendenciaScore: 67,
    crecimiento: 46,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:12:00Z',
    senalesActividad: { oyentes: 3900, interacciones: 2800, compartidos: 1800, comentarios: 3600 },
    posicion: 21,
    cambioPosicion: 14,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Electronic%20music%20radio%20DJ%20set%20in%20dark%20club-like%20studio%20with%20neon%20laser%20lights%2C%20smoke%20machine%2C%20professional%20DJ%20equipment%2C%20high%20energy%20atmosphere%2C%20concert%20photography&width=800&height=500&seq=tendencia-prog-05&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Electronic%20DJ%20radio%20program%20logo%20with%20sound%20waveform%20and%20bass%20drop%20design%2C%20cyan%20and%20purple%20colors%2C%20circular%20emblem&width=200&height=200&seq=tendencia-logo-pg-05&orientation=squarish',
    badge: 'Fenómeno Nocturno',
    estaEnVivo: false,
    frecuencia: '96.50 RFW',
    horario: 'Sáb 22:00-02:00',
    ciudad: 'Miami',
    pais: 'Estados Unidos',
    categoria: 'Electrónica',
    radioNombre: 'Electro Vibes FM',
    locutorNombre: 'DJ Spark',
    miembros: 2800,
  },
  {
    id: 22,
    tipo: 'locutor',
    nombre: 'Javier Solís',
    subtitulo: 'Deportes Total Radio · 120.00 RFW',
    tendenciaScore: 65,
    crecimiento: 28,
    direccion: 'estable',
    ultimaActualizacion: '2026-06-21T14:11:00Z',
    senalesActividad: { oyentes: 3800, interacciones: 2400, compartidos: 1300, comentarios: 3200 },
    posicion: 22,
    cambioPosicion: 0,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Passionate%20male%20sports%20broadcaster%20gesturing%20while%20speaking%2C%20headphones%20around%20neck%2C%20dynamic%20studio%20with%20sports%20memorabilia%2C%20professional%20lighting%2C%20editorial%20portrait&width=800&height=500&seq=tendencia-locutor-05&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20male%20broadcaster%20avatar%20with%20headset%2C%20professional%20look%2C%20circular%20frame&width=200&height=200&seq=tendencia-logo-lc-05&orientation=squarish',
    badge: 'Voz Deportiva',
    estaEnVivo: true,
    frecuencia: '120.00 RFW',
    horario: 'Lun-Vie 15:00-19:00',
    ciudad: 'Dallas',
    pais: 'Estados Unidos',
    categoria: 'Deportes',
    radioNombre: 'Deportes Total Radio',
    locutorNombre: 'Javier Solís',
    miembros: 0,
  },
  {
    id: 23,
    tipo: 'club',
    nombre: 'Familia Mexicana FM',
    subtitulo: 'La Mexicana FM · Club Oficial',
    tendenciaScore: 63,
    crecimiento: 27,
    direccion: 'estable',
    ultimaActualizacion: '2026-06-21T14:10:00Z',
    senalesActividad: { oyentes: 0, interacciones: 1700, compartidos: 900, comentarios: 2100, miembros: 7200 },
    posicion: 23,
    cambioPosicion: -1,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Mexican%20family%20gathering%20community%20event%20with%20food%20and%20music%2C%20outdoor%20fiesta%20atmosphere%2C%20colorful%20decorations%2C%20warm%20evening%20lighting%2C%20cultural%20documentary%20photography&width=800&height=500&seq=tendencia-club-05&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20family%20community%20club%20logo%20with%20heart%20and%20sombrero%2C%20circular%20emblem%2C%20warm%20orange%20and%20green%20colors&width=200&height=200&seq=tendencia-logo-cl-05&orientation=squarish',
    badge: 'Club Familiar',
    estaEnVivo: false,
    frecuencia: '103.50 RFW',
    horario: 'Activo 24/7',
    ciudad: 'Houston',
    pais: 'Estados Unidos',
    categoria: 'Regional Mexicana',
    radioNombre: 'La Mexicana FM',
    locutorNombre: '',
    miembros: 7200,
  },
  {
    id: 24,
    tipo: 'evento',
    nombre: 'Andrés Castillo Unplugged',
    subtitulo: 'Rock Total Radio · 28 Jun 2026',
    tendenciaScore: 61,
    crecimiento: 51,
    direccion: 'subiendo',
    ultimaActualizacion: '2026-06-21T14:09:00Z',
    senalesActividad: { oyentes: 0, interacciones: 3200, compartidos: 1900, comentarios: 4300, participantes: 7200 },
    posicion: 24,
    cambioPosicion: 16,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Intimate%20acoustic%20concert%20with%20guitarist%20on%20small%20stage%2C%20warm%20candlelit%20venue%2C%20cozy%20audience%20seated%20around%2C%20soft%20amber%20lighting%2C%20unplugged%20music%20event%20photography&width=800&height=500&seq=tendencia-evento-04&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%2C%20circular%20design%20red%20and%20black%20colors&width=200&height=200&seq=tendencia-logo-ev-04&orientation=squarish',
    badge: 'Íntimo y Especial',
    estaEnVivo: false,
    frecuencia: '87.01 RFW',
    horario: '28 Jun 19:00',
    ciudad: 'Buenos Aires',
    pais: 'Argentina',
    categoria: 'Rock',
    radioNombre: 'Rock Total Radio',
    locutorNombre: 'Andrés Castillo',
    miembros: 0,
  },
  {
    id: 25,
    tipo: 'evento',
    nombre: 'Batalla de Freestyle en Vivo',
    subtitulo: 'Radio Hip Hop Nation · Hoy 20:00',
    tendenciaScore: 60,
    crecimiento: 74,
    direccion: 'explosivo',
    ultimaActualizacion: '2026-06-21T14:08:00Z',
    senalesActividad: { oyentes: 0, interacciones: 3900, compartidos: 2600, comentarios: 5800, participantes: 10200 },
    posicion: 25,
    cambioPosicion: 20,
    imagenUrl: 'https://readdy.ai/api/search-image?query=Hip%20hop%20freestyle%20rap%20battle%20with%20two%20MCs%20facing%20off%20on%20stage%2C%20energetic%20crowd%20with%20hands%20raised%2C%20urban%20venue%20with%20neon%20lights%2C%20intense%20competition%20atmosphere%2C%20dynamic%20concert%20photography&width=800&height=500&seq=tendencia-evento-05&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Hip%20hop%20battle%20event%20logo%20with%20microphone%20and%20fire%2C%20circular%20design%2C%20gold%20and%20black%20colors&width=200&height=200&seq=tendencia-logo-ev-05&orientation=squarish',
    badge: 'HOY Imperdible',
    estaEnVivo: false,
    frecuencia: '170.40 RFW',
    horario: 'Hoy 20:00',
    ciudad: 'Atlanta',
    pais: 'Estados Unidos',
    categoria: 'Hip Hop',
    radioNombre: 'Radio Hip Hop Nation',
    locutorNombre: 'Big Mike',
    miembros: 0,
  },
];

export function getTendenciasPorTipo(tipo: TipoTendencia): TendenciaEnVivo[] {
  return tendenciasEnVivo.filter(t => t.tipo === tipo).sort((a, b) => a.posicion - b.posicion);
}

export function getTop5Global(): TendenciaEnVivo[] {
  return tendenciasEnVivo.slice(0, 5);
}

export function getTendenciasExplosivas(): TendenciaEnVivo[] {
  return tendenciasEnVivo.filter(t => t.direccion === 'explosivo');
}

export function getTendenciasSubiendo(): TendenciaEnVivo[] {
  return tendenciasEnVivo.filter(t => t.direccion === 'subiendo' || t.direccion === 'explosivo');
}

export function getTopPorCategoria(): { tipo: TipoTendencia; item: TendenciaEnVivo }[] {
  return tiposTendencia.map(tipo => {
    const items = getTendenciasPorTipo(tipo.id);
    return { tipo: tipo.id, item: items[0] };
  }).filter(x => x.item);
}

export function formatearNumero(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}